import React, { useState, useEffect } from 'react';
import useAxiosInstance from '../../services/useAxiosInstance';
import { useDispatch, useSelector } from 'react-redux';
import { setTimeZone, setAvailableTimeZones } from '../../redux/timeSlice';
import { Clock, Save } from 'lucide-react';

const SetTimeZone = () => {
    const axiosInstance = useAxiosInstance();
    const dispatch = useDispatch();
    const { availableTimeZones } = useSelector((state) => state.timeZone);
    const userId = useSelector((state) => state.auth.userId);

    const [selectedTimeZone, setSelectedTimeZone] = useState('');
    const [formattedDateTime, setFormattedDateTime] = useState('');
    const [timeZoneError, setTimeZoneError] = useState('');
    const [userTimeZoneId, setUserTimeZoneId] = useState(null);
    const [saveSuccess, setSaveSuccess] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch available time zones if not already loaded
                if (availableTimeZones.length === 0) {
                    const timeZonesResponse = await axiosInstance.get(`/api/public/timeZone/getAll`);
                    if (Array.isArray(timeZonesResponse.data)) {
                        dispatch(setAvailableTimeZones(timeZonesResponse.data));
                    }
                }

                // Fetch user's current time zone
                if (userId) {
                    const userTimeZoneResponse = await axiosInstance.get(`/api/public/userTimeZone/getByUserId/${userId}`);
                    if (userTimeZoneResponse.data && userTimeZoneResponse.data.timeZoneId) {
                        // Find the time zone object that matches the ID
                        const timeZoneObj = availableTimeZones.find(tz => tz.id === userTimeZoneResponse.data.timeZoneId);
                        if (timeZoneObj) {
                            setSelectedTimeZone(timeZoneObj.name);
                            setUserTimeZoneId(userTimeZoneResponse.data.id);
                            updateDateTime(timeZoneObj.name);
                        }
                    }
                }
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };

        fetchData();
    }, [userId, availableTimeZones, dispatch]);

    const handleTimeZoneChange = (e) => {
        const newTimeZone = e.target.value;
        setSelectedTimeZone(newTimeZone);
        updateDateTime(newTimeZone);
        setSaveSuccess(false); // Reset success message when selection changes
    };

    const updateDateTime = (timeZone) => {
        try {
            const now = new Date();
            const options = {
                timeZone,
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                timeZoneName: 'short'
            };
            const formatter = new Intl.DateTimeFormat([], options);
            setFormattedDateTime(formatter.format(now));
            setTimeZoneError('');
        } catch (error) {
            console.error('Invalid time zone:', error);
            setTimeZoneError('Invalid time zone selected');
            setFormattedDateTime('');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (selectedTimeZone && userId) {
            try {
                // Find the time zone ID for the selected time zone
                const timeZoneObj = availableTimeZones.find(tz => tz.name === selectedTimeZone);
                if (!timeZoneObj) {
                    throw new Error('Selected time zone not found');
                }

                // Test if the timezone is valid
                new Intl.DateTimeFormat([], { timeZone: selectedTimeZone });

                // Prepare the payload
                const payload = {
                    timeZoneId: timeZoneObj.id,
                    userId: userId
                };

                // If we have a userTimeZoneId, this is an update, otherwise it's a create
                let response;
                if (userTimeZoneId) {
                    payload.id = userTimeZoneId;
                    response = await axiosInstance.put(`/hoa/api/public/userTimeZone/update`, payload);
                } else {
                    response = await axiosInstance.post(`/hoa/api/public/userTimeZone/create`, payload);
                    setUserTimeZoneId(response.data.id);
                }

                dispatch(setTimeZone(selectedTimeZone));
                setSaveSuccess(true);
                setTimeout(() => {
                    setSaveSuccess(false);
                }, 3000);
            } catch (error) {
                console.error('Error saving time zone:', error);
                setTimeZoneError('Error saving time zone. Please try again.');
            }
        }
    };

    return (
        <div className="bg-gray-50 min-h-screen p-4">
            <div className="max-w-7xl mx-auto">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-2xl font-bold text-gray-800">Set Time Zone</h1>
                    <div className="flex items-center">
                        <Clock size={18} className="text-blue-500 mr-2" />
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
                    <div className="p-6">
                        <form onSubmit={handleSubmit}>
                            <div className="mb-6">
                                <label htmlFor="timeZone" className="block text-sm font-medium text-gray-700 mb-1">
                                    Select Time Zone
                                </label>
                                <select
                                    id="timeZone"
                                    value={selectedTimeZone}
                                    onChange={handleTimeZoneChange}
                                    className="w-full rounded-md border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    required
                                >
                                    <option value="">Select a time zone...</option>
                                    {availableTimeZones.map((zone, index) => (
                                        <option key={index} value={zone.name}>
                                            {zone.timeZone} {zone.description}
                                        </option>
                                    ))}
                                </select>
                                {timeZoneError && (
                                    <p className="mt-2 text-sm text-red-600">{timeZoneError}</p>
                                )}
                            </div>

                            {formattedDateTime && (
                                <div className="mb-6 p-4 bg-gray-50 rounded-md border border-gray-200">
                                    <h3 className="text-sm font-medium text-gray-700 mb-2">Current Date and Time:</h3>
                                    <p className="text-lg text-gray-900">{formattedDateTime}</p>
                                </div>
                            )}

                            {saveSuccess && (
                                <div className="mb-6 p-3 bg-green-50 text-green-700 rounded-md border border-green-200">
                                    Time zone successfully set to: {selectedTimeZone}
                                </div>
                            )}

                            <div className="flex justify-end">
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center gap-2"
                                    disabled={!selectedTimeZone}
                                >
                                    <Save size={18} />
                                    <span>Set Time Zone</span>
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );

};

export default SetTimeZone;